import { useState, useEffect, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion, AnimatePresence } from 'framer-motion'
import {
  FileSearch,
  Play,
  X,
  AlertCircle,
  Loader2,
  RefreshCw,
  Download,
  CheckCircle2,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { NativeSelect } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { Checkbox } from '@/components/ui/checkbox'
import { evaluationsApi } from '@/services/api/evaluations'
import type { ArticlePreview, JobStatusResponse } from '@/types/api'
import { cn } from '@/lib/utils'

// Helper to get evaluation flag badge styles
function getEvaluationBadgeStyle(flag: string | null | undefined) {
  switch (flag) {
    case 'Critical change needed':
      return 'bg-red-100 text-red-800 border-red-200'
    case 'Needs improvement':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200'
    case 'High quality':
      return 'bg-green-100 text-green-800 border-green-200'
    default:
      return 'bg-gray-100 text-gray-600 border-gray-200'
  }
}

function getEvaluationFlagShort(flag: string | null | undefined) {
  switch (flag) {
    case 'Critical change needed':
      return 'Critical'
    case 'Needs improvement':
      return 'Improve'
    case 'High quality':
      return 'Good'
    default:
      return 'New'
  }
}

// Compact Article Row Component
function ArticleRow({
  article,
  selected,
  onToggle,
}: {
  article: ArticlePreview
  selected: boolean
  onToggle: () => void
}) {
  return (
    <div
      className={cn(
        'group flex items-center gap-4 px-4 py-3 border-b border-border/50 cursor-pointer transition-colors',
        'hover:bg-muted/50',
        selected && 'bg-primary/5 hover:bg-primary/10'
      )}
      onClick={onToggle}
    >
      {/* Checkbox */}
      <div className="flex-shrink-0">
        <Checkbox
          checked={selected}
          onCheckedChange={onToggle}
          onClick={(e) => e.stopPropagation()}
          className="data-[state=checked]:bg-primary data-[state=checked]:border-primary"
        />
      </div>

      {/* Article ID */}
      <span className="flex-shrink-0 w-28 font-mono text-sm text-muted-foreground">
        {article.display_number}
      </span>

      {/* Title */}
      <span className="flex-1 truncate font-medium">
        {article.article_title}
      </span>

      {/* Author */}
      <span className="flex-shrink-0 w-40 truncate text-sm text-muted-foreground">
        {article.author_name || '—'}
      </span>

      {/* Evaluation Status Badge */}
      <div className="flex-shrink-0">
        <span className={cn(
          'text-xs px-2 py-0.5 rounded border',
          getEvaluationBadgeStyle(article.evaluation_flag)
        )}>
          {article.is_evaluated ? getEvaluationFlagShort(article.evaluation_flag) : 'New'}
        </span>
      </div>

      {/* Metadata Tags */}
      <div className="flex-shrink-0 flex items-center gap-2 opacity-70 group-hover:opacity-100 transition-opacity">
        <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded">
          {article.knowledge_base}
        </span>
        <span className="text-xs text-muted-foreground px-2 py-0.5 bg-muted rounded hidden lg:inline">
          {article.category}
        </span>
        <span className="text-xs text-muted-foreground uppercase w-6 text-center">
          {article.language}
        </span>
      </div>
    </div>
  )
}

// List Header Component
function ListHeader({
  onSelectAll,
  allSelected,
  someSelected,
}: {
  onSelectAll: () => void
  allSelected: boolean
  someSelected: boolean
}) {
  return (
    <div className="flex items-center gap-4 px-4 py-2 border-b border-border bg-muted/30 text-sm text-muted-foreground sticky top-0 z-10">
      {/* Select All Checkbox */}
      <div className="flex-shrink-0">
        <Checkbox
          checked={allSelected}
          onCheckedChange={onSelectAll}
          className={cn(
            someSelected && !allSelected && 'data-[state=checked]:bg-muted'
          )}
        />
      </div>

      {/* Column Headers */}
      <span className="flex-shrink-0 w-28">Article ID</span>
      <span className="flex-1">Title</span>
      <span className="flex-shrink-0 w-40">Author</span>
      <span className="flex-shrink-0 w-16 text-center">Status</span>
      <span className="flex-shrink-0 w-48 text-right hidden lg:block">Metadata</span>
    </div>
  )
}

// Active Job Card Component
function ActiveJobCard({
  job,
  onCancel,
}: {
  job: JobStatusResponse
  onCancel: () => void
}) {
  const progress = job.progress.percentage

  return (
    <Card className="border-primary/50">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="font-medium">{job.job_name || job.job_id}</span>
          <Button variant="ghost" size="icon" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <Progress value={progress} className="mb-2" />
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>
            {job.progress.processed_articles} of {job.progress.total_articles} articles
          </span>
          <span>{progress.toFixed(0)}%</span>
        </div>
      </CardContent>
    </Card>
  )
}

// Completed Job Card Component
function CompletedJobCard({
  job,
  onDownload,
  onDismiss,
  isDownloading,
}: {
  job: JobStatusResponse
  onDownload: () => void
  onDismiss: () => void
  isDownloading: boolean
}) {
  const isSuccess = job.status === 'completed'
  const isFailed = job.status === 'failed'

  return (
    <Card className={cn(
      'border-2',
      isSuccess && 'border-green-500/50 bg-green-50/50',
      isFailed && 'border-red-500/50 bg-red-50/50',
      !isSuccess && !isFailed && 'border-yellow-500/50 bg-yellow-50/50'
    )}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isSuccess ? (
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            ) : isFailed ? (
              <XCircle className="h-6 w-6 text-red-600" />
            ) : (
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            )}
            <div>
              <p className="font-medium">{job.job_name || 'Evaluation Job'}</p>
              <p className="text-sm text-muted-foreground">
                {isSuccess && `${job.progress.successful_articles} articles evaluated successfully`}
                {isFailed && (job.error_message || 'Job failed')}
                {!isSuccess && !isFailed && 'Job was cancelled'}
                {job.progress.failed_articles > 0 && isSuccess && (
                  <span className="text-yellow-600 ml-2">
                    ({job.progress.failed_articles} failed)
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isSuccess && job.progress.successful_articles > 0 && (
              <Button
                onClick={onDownload}
                disabled={isDownloading}
                size="sm"
                variant="outline"
                className="bg-white"
              >
                {isDownloading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Download className="h-4 w-4 mr-2" />
                )}
                Download Excel
              </Button>
            )}
            <Button variant="ghost" size="icon" onClick={onDismiss}>
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// LocalStorage keys for persisting job state
const ACTIVE_JOB_KEY = 'evaluations_active_job_id'
const COMPLETED_JOB_KEY = 'evaluations_completed_job'

// Main Page Component
export function EvaluationsPage() {
  const queryClient = useQueryClient()

  // State - initialize activeJobId from localStorage
  const [knowledgeBase, setKnowledgeBase] = useState('')
  const [category, setCategory] = useState('')
  const [evaluationStatus, setEvaluationStatus] = useState<'all' | 'not_evaluated' | 'evaluated'>('not_evaluated')
  const [selectedArticles, setSelectedArticles] = useState<Set<string>>(new Set())
  const [page, setPage] = useState(1)
  const [allArticles, setAllArticles] = useState<ArticlePreview[]>([])
  const [activeJobId, setActiveJobId] = useState<string | null>(() => {
    // Restore from localStorage on initial load
    if (typeof window !== 'undefined') {
      return localStorage.getItem(ACTIVE_JOB_KEY)
    }
    return null
  })
  const [completedJob, setCompletedJob] = useState<JobStatusResponse | null>(() => {
    // Restore completed job from localStorage on initial load
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(COMPLETED_JOB_KEY)
      if (stored) {
        try {
          return JSON.parse(stored)
        } catch {
          return null
        }
      }
    }
    return null
  })
  const [jobName, setJobName] = useState('')

  // Persist activeJobId to localStorage
  useEffect(() => {
    if (activeJobId) {
      localStorage.setItem(ACTIVE_JOB_KEY, activeJobId)
    } else {
      localStorage.removeItem(ACTIVE_JOB_KEY)
    }
  }, [activeJobId])

  // Persist completedJob to localStorage
  useEffect(() => {
    if (completedJob) {
      localStorage.setItem(COMPLETED_JOB_KEY, JSON.stringify(completedJob))
    } else {
      localStorage.removeItem(COMPLETED_JOB_KEY)
    }
  }, [completedJob])

  // Fetch filters
  const { data: filters } = useQuery({
    queryKey: ['evaluations', 'filters'],
    queryFn: () => evaluationsApi.getFilters(),
  })

  // Reset pagination when filters change
  const resetPagination = useCallback(() => {
    setPage(1)
    setAllArticles([])
  }, [])

  // Fetch article preview
  const { data: preview, isLoading, isFetching, isError } = useQuery({
    queryKey: ['evaluations', 'preview', knowledgeBase, category, evaluationStatus, page],
    queryFn: () =>
      evaluationsApi.preview({
        knowledge_base: knowledgeBase || undefined,
        categories: category ? [category] : undefined,
        evaluation_status: evaluationStatus,
        page,
        limit: 100,
      }),
    staleTime: 30000, // Cache for 30 seconds
    placeholderData: (previousData) => previousData, // Keep showing previous data while fetching
  })

  // Accumulate articles when new data arrives
  useEffect(() => {
    if (preview?.articles) {
      if (page === 1) {
        setAllArticles(preview.articles)
      } else {
        setAllArticles((prev) => {
          const existingIds = new Set(prev.map((a) => a.display_number))
          const newArticles = preview.articles.filter((a) => !existingIds.has(a.display_number))
          return [...prev, ...newArticles]
        })
      }
    }
  }, [preview, page])

  const hasMore = preview?.pagination ? preview.pagination.page < preview.pagination.pages : false
  const isLoadingMore = isFetching && page > 1

  // Only show loading skeleton on initial load (no cached data)
  // Don't show skeleton during background refetches
  const previewLoading = isLoading && !preview && page === 1

  // Fetch active job status (polling every 1.5 seconds for responsive progress updates)
  const { data: activeJob } = useQuery({
    queryKey: ['evaluations', 'job', activeJobId],
    queryFn: () => evaluationsApi.getJobStatus(activeJobId!),
    enabled: !!activeJobId,
    refetchInterval: (query) => {
      const data = query.state.data
      if (!data) return 1500
      if (['completed', 'failed', 'cancelled'].includes(data.status)) {
        return false
      }
      return 1500
    },
  })

  // Move job to completed state when finished
  if (activeJob && ['completed', 'failed', 'cancelled'].includes(activeJob.status) && activeJobId) {
    setCompletedJob(activeJob)
    setActiveJobId(null)
    queryClient.invalidateQueries({ queryKey: ['evaluations'] })
  }

  // Export mutation
  const exportMutation = useMutation({
    mutationFn: (jobId?: string) => evaluationsApi.exportToExcel({ job_id: jobId }),
  })

  // Submit job mutation
  const submitJob = useMutation({
    mutationFn: () =>
      evaluationsApi.submit({
        knowledge_base: knowledgeBase || undefined,
        categories: category ? [category] : undefined,
        selection_mode: selectedArticles.size > 0 ? 'include' : 'all',
        article_ids: selectedArticles.size > 0 ? Array.from(selectedArticles) : undefined,
        job_name: jobName || undefined,
      }),
    onSuccess: (data) => {
      setActiveJobId(data.job_id)
      setSelectedArticles(new Set())
      setJobName('')
    },
  })

  // Cancel job mutation
  const cancelJob = useMutation({
    mutationFn: (jobId: string) => evaluationsApi.cancelJob(jobId),
    onSuccess: () => {
      setActiveJobId(null)
      queryClient.invalidateQueries({ queryKey: ['evaluations'] })
    },
  })

  // Toggle article selection
  const toggleArticle = (displayNumber: string) => {
    const newSelected = new Set(selectedArticles)
    if (newSelected.has(displayNumber)) {
      newSelected.delete(displayNumber)
    } else {
      newSelected.add(displayNumber)
    }
    setSelectedArticles(newSelected)
  }

  // Select all / deselect all
  const toggleSelectAll = () => {
    if (selectedArticles.size === allArticles.length) {
      setSelectedArticles(new Set())
    } else {
      setSelectedArticles(new Set(allArticles.map((a) => a.display_number)))
    }
  }

  // Get categories for selected knowledge base
  const categories = knowledgeBase && filters?.categories[knowledgeBase]
    ? filters.categories[knowledgeBase]
    : []

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-primary/10">
            <FileSearch className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Evaluate Articles</h1>
            <p className="text-muted-foreground">
              Select articles to analyze for quality and issues
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={() => exportMutation.mutate(undefined)}
          disabled={exportMutation.isPending}
        >
          {exportMutation.isPending ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          Download All Results
        </Button>
      </div>

      {/* Active Job */}
      <AnimatePresence>
        {activeJob && !['completed', 'failed', 'cancelled'].includes(activeJob.status) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <ActiveJobCard
              job={activeJob}
              onCancel={() => cancelJob.mutate(activeJob.job_id)}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Completed Job */}
      <AnimatePresence>
        {completedJob && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
          >
            <CompletedJobCard
              job={completedJob}
              onDownload={() => exportMutation.mutate(completedJob.job_id)}
              onDismiss={() => setCompletedJob(null)}
              isDownloading={exportMutation.isPending}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-1 block">Knowledge Base</label>
              <NativeSelect
                value={knowledgeBase}
                onChange={(e) => {
                  setKnowledgeBase(e.target.value)
                  setCategory('')
                  resetPagination()
                }}
                options={[
                  { value: '', label: 'All Knowledge Bases' },
                  ...(filters?.knowledge_bases || []).map((kb) => ({
                    value: kb,
                    label: kb,
                  })),
                ]}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-1 block">Category</label>
              <NativeSelect
                value={category}
                onChange={(e) => { setCategory(e.target.value); resetPagination() }}
                disabled={!knowledgeBase}
                options={[
                  { value: '', label: 'All Categories' },
                  ...categories.map((cat) => ({
                    value: cat,
                    label: cat,
                  })),
                ]}
              />
            </div>
            <div className="flex-1 min-w-[200px]">
              <label className="text-sm font-medium mb-1 block">Evaluation Status</label>
              <NativeSelect
                value={evaluationStatus}
                onChange={(e) => { setEvaluationStatus(e.target.value as 'all' | 'not_evaluated' | 'evaluated'); resetPagination() }}
                options={[
                  { value: 'not_evaluated', label: 'Not Evaluated (New)' },
                  { value: 'evaluated', label: 'Already Evaluated' },
                  { value: 'all', label: 'All Articles' },
                ]}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => {
                  setKnowledgeBase('')
                  setCategory('')
                  setEvaluationStatus('not_evaluated')
                  setSelectedArticles(new Set())
                  resetPagination()
                }}
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Reset
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Action Bar */}
      <div className="flex items-center justify-between py-3 px-4 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-4">
          <span className="text-sm font-medium">
            {allArticles.length} of {preview?.total_articles || 0} articles
          </span>
          {isFetching && !previewLoading && (
            <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
          )}
          {selectedArticles.size > 0 && (
            <Badge variant="secondary" className="font-normal">
              {selectedArticles.size} selected
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Input
            placeholder="Job name (optional)"
            value={jobName}
            onChange={(e) => setJobName(e.target.value)}
            className="w-48 h-9"
          />
          <Button
            onClick={() => submitJob.mutate()}
            disabled={submitJob.isPending || !!activeJobId}
            size="sm"
          >
            {submitJob.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            {selectedArticles.size > 0
              ? `Evaluate ${selectedArticles.size} Selected`
              : `Evaluate All`}
          </Button>
        </div>
      </div>

      {/* Article List */}
      <Card className="overflow-hidden">
        {previewLoading ? (
          <div className="divide-y divide-border/50">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-4 py-3">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-5 w-20" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-destructive" />
            <p className="text-lg font-medium">Failed to load articles</p>
            <p className="text-muted-foreground">
              Please check your connection and try again
            </p>
          </CardContent>
        ) : !preview?.articles?.length ? (
          <CardContent className="p-12 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <p className="text-lg font-medium">No articles found</p>
            <p className="text-muted-foreground">
              Try adjusting your filters to see more results
            </p>
          </CardContent>
        ) : (
          <>
            <ListHeader
              onSelectAll={toggleSelectAll}
              allSelected={selectedArticles.size === allArticles.length && allArticles.length > 0}
              someSelected={selectedArticles.size > 0 && selectedArticles.size < allArticles.length}
            />
            <div className="max-h-[600px] overflow-y-auto">
              {allArticles.map((article) => (
                <ArticleRow
                  key={article.display_number}
                  article={article}
                  selected={selectedArticles.has(article.display_number)}
                  onToggle={() => toggleArticle(article.display_number)}
                />
              ))}
              {/* Load More Button */}
              {hasMore && (
                <div className="flex justify-center py-4 border-t border-border/50">
                  <Button
                    variant="outline"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={isLoadingMore}
                  >
                    {isLoadingMore ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : null}
                    {isLoadingMore
                      ? 'Loading...'
                      : `Load More (${allArticles.length} of ${preview?.total_articles || 0})`}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </Card>
    </div>
  )
}
